<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import McpServerTools from './McpServerTools.vue';

const props = defineProps({
  server: {
    type: Object,
    required: true,
  },
  isTestingConnection: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['test', 'edit', 'delete']);

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const isProtectedServer = computed(() => Boolean(props.server.internal));

const hasLoadedTools = ref(false);
const serverTools = computed(() => {
  return getters['mcpServers/getServerTools'].value(props.server.id);
});

const statusTextColor = computed(() => {
  switch (props.server.status) {
    case 'active':
      return 'text-green-600';
    case 'error':
      return 'text-red-600';
    default:
      return 'text-slate-500';
  }
});

const statusBgColor = computed(() => {
  switch (props.server.status) {
    case 'active':
      return 'bg-green-100';
    case 'error':
      return 'bg-red-100';
    default:
      return 'bg-slate-100';
  }
});

const statusText = computed(() => {
  switch (props.server.status) {
    case 'active':
      return t('INTEGRATION_SETTINGS.MCP_SERVERS.STATUS.CONNECTED');
    case 'inactive':
      return t('INTEGRATION_SETTINGS.MCP_SERVERS.STATUS.DISCONNECTED');
    case 'error':
      return t('INTEGRATION_SETTINGS.MCP_SERVERS.STATUS.ERROR');
    default:
      return t('INTEGRATION_SETTINGS.MCP_SERVERS.STATUS.DISCONNECTED');
  }
});

const statusDotColor = computed(() => {
  switch (props.server.status) {
    case 'active':
      return 'bg-green-500';
    case 'error':
      return 'bg-red-500';
    default:
      return 'bg-slate-400';
  }
});

const canShowTools = computed(() => {
  return props.server.status === 'active';
});

const loadTools = async () => {
  if (!canShowTools.value) return;

  try {
    await store.dispatch('mcpServers/getTools', props.server.id);
    hasLoadedTools.value = true;
  } catch (error) {
    console.error('Error loading tools:', error);
  }
};

// Watch for status changes and reload tools
watch(
  () => props.server.status,
  (newStatus, oldStatus) => {
    if (newStatus === 'active' && oldStatus !== 'active') {
      loadTools();
    }
  }
);

// Auto-load tools if server is active on mount
onMounted(() => {
  if (canShowTools.value) {
    loadTools();
  }
});
</script>

<template>
  <CardLayout layout="column">
    <div class="flex flex-col w-full">
      <div class="flex items-start justify-between">
        <div class="flex flex-col flex-1 min-w-0 gap-2">
          <div class="flex justify-between items-start gap-3 w-full">
            <span class="text-base font-medium text-n-slate-12 line-clamp-1">
              {{ server.label }}
            </span>
            <span
              class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md flex-shrink-0"
              :class="[statusTextColor, statusBgColor]"
            >
              <span
                class="w-1.5 h-1.5 rounded-full mr-1"
                :class="statusDotColor"
              />
              {{ statusText }}
            </span>
          </div>
          <div
            v-if="!server.internal && server.url"
            class="text-sm text-n-slate-11 line-clamp-1"
          >
            {{ server.url }}
          </div>
        </div>

        <div class="flex items-center gap-2 ml-4">
          <Button
            v-if="!isProtectedServer && server.status !== 'active'"
            variant="faded"
            size="sm"
            color="slate"
            icon="i-lucide-wifi"
            :loading="isTestingConnection"
            @click="emit('test')"
          />
          <Button
            v-if="!isProtectedServer"
            variant="faded"
            size="sm"
            color="slate"
            icon="i-lucide-pencil-line"
            @click="emit('edit')"
          />
          <Button
            v-if="!isProtectedServer"
            variant="faded"
            color="ruby"
            size="sm"
            icon="i-lucide-trash-2"
            @click="emit('delete')"
          />
        </div>
      </div>

      <McpServerTools
        v-if="canShowTools"
        :tools="serverTools"
        :has-loaded="hasLoadedTools"
      />
    </div>
  </CardLayout>
</template>
